import { useRef, useEffect, useState, useCallback } from 'react'
import type { BlockAnimation } from '../editor/types'

interface UseBlockAnimationOptions {
  animation?: BlockAnimation
  variableValues: Record<string, number | string | boolean>
}

export function useBlockAnimation({ animation, variableValues }: UseBlockAnimationOptions) {
  const ref = useRef<HTMLDivElement>(null)
  const [isVisible, setIsVisible] = useState(false)
  const [animationClass, setAnimationClass] = useState('')
  const prevValuesRef = useRef(variableValues)
  const hasAppearedRef = useRef(false)

  // on-appear trigger via IntersectionObserver
  useEffect(() => {
    if (!animation || animation.trigger !== 'on-appear') return
    const el = ref.current
    if (!el) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAppearedRef.current) {
          hasAppearedRef.current = true
          setTimeout(() => {
            setIsVisible(true)
            setAnimationClass(getAnimationClass(animation.effect))
          }, animation.delay)
        }
      },
      { threshold: 0.1 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [animation])

  // on-variable-change trigger
  useEffect(() => {
    if (!animation || animation.trigger !== 'on-variable-change') return
    const prev = prevValuesRef.current
    const watchVar = animation.watchVariable
    const changed = watchVar
      ? prev[watchVar] !== variableValues[watchVar]
      : Object.keys(variableValues).some((k) => prev[k] !== variableValues[k])
    prevValuesRef.current = variableValues

    if (changed) {
      setAnimationClass('')
      requestAnimationFrame(() => {
        setAnimationClass(getAnimationClass(animation.effect))
      })
    }
  }, [animation, variableValues])

  // For on-appear: start hidden, then animate in
  const style: React.CSSProperties = animation?.trigger === 'on-appear' && !isVisible
    ? { opacity: 0, transform: getInitialTransform(animation.effect) }
    : {}

  const duration = animation?.duration ?? 400

  return {
    ref,
    className: animationClass,
    style: {
      ...style,
      animationDuration: `${duration}ms`,
      transitionDuration: `${duration}ms`,
    },
  }
}

function getAnimationClass(effect: string): string {
  switch (effect) {
    case 'fade': return 'animate-fade-in'
    case 'vibrate': return 'animate-vibrate'
    case 'slide-up': return 'animate-slide-up'
    case 'slide-down': return 'animate-slide-down'
    case 'slide-left': return 'animate-slide-left'
    case 'slide-right': return 'animate-slide-right'
    case 'typing': return 'animate-typing'
    default: return ''
  }
}

function getInitialTransform(effect: string): string {
  switch (effect) {
    case 'slide-up': return 'translateY(20px)'
    case 'slide-down': return 'translateY(-20px)'
    case 'slide-left': return 'translateX(20px)'
    case 'slide-right': return 'translateX(-20px)'
    default: return ''
  }
}
